import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AspectRatio,
  Box,
  IconButton,
  Flex,
  Text,
  useColorModeValue,
  Badge,
  Wrap,
  WrapItem,
  Tooltip,
  Divider,
  VStack,
  Heading,
  HStack
} from '@chakra-ui/react';
import { CaretLeft, CaretRight, Clock } from '@phosphor-icons/react';
import { mapFeature, normalizeFeature } from '../../utils/featureUtils';

// Format feature label for display
const formatFeatureLabel = (label) => {
  return String(label)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Helper function to safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return 'Unknown date';
  
  try {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    return 'Unknown date';
  }
};

const getEmbedUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    
    // YouTube
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      let videoId = '';
      if (hostname.includes('youtube.com')) {
        videoId = urlObj.searchParams.get('v');
      } else {
        videoId = urlObj.pathname.slice(1);
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Vimeo
    if (hostname.includes('vimeo.com')) {
      const videoId = urlObj.pathname.slice(1);
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    // Dailymotion
    if (hostname.includes('dailymotion.com')) {
      const videoId = urlObj.pathname.split('/')[2];
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    }
    
    // Facebook videos - Note: FB requires additional setup for embed
    if (hostname.includes('facebook.com') || hostname.includes('fb.watch')) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
    }
    
    return url;
  } catch {
    return url;
  }
};

const VideoModal = ({
  isOpen,
  onClose,
  videoUrl,
  videoData,
  hasMultiple,
  onPrevious,
  onNext
}) => {
  const embedUrl = getEmbedUrl(videoUrl || videoData?.url);
  const descriptionBg = useColorModeValue('gray.50', 'gray.700');
  const featuresBg = useColorModeValue('gray.100', 'gray.700');
  const autoBadgeBg = useColorModeValue('purple.50', 'purple.900');
  const manualBadgeBg = useColorModeValue('blue.50', 'blue.900');
  const badgeColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const dateColor = useColorModeValue('gray.500', 'gray.500');
  const headerBg = useColorModeValue('white', 'gray.800');
  const contentBg = useColorModeValue('white', 'gray.900');

  console.log("VideoModal - videoData:", videoData); // Debug log

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={contentBg}>
        <ModalHeader 
          color={useColorModeValue('gray.800', 'white')}
          borderBottom="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          bg={headerBg}
        >
          <Text>{videoData?.title || "Video"}</Text>
        </ModalHeader>
        <ModalCloseButton color={useColorModeValue('gray.800', 'white')} />
        <ModalBody p={0}>
          <VStack align="stretch" spacing={0}>
            <Box position="relative">
              <AspectRatio ratio={16/9}>
                <iframe
                  src={embedUrl}
                  title="Video Player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
              
              {hasMultiple && (
                <Flex
                  position="absolute"
                  top="50%"
                  left={0}
                  right={0}
                  transform="translateY(-50%)"
                  justify="space-between"
                  px={4}
                  pointerEvents="none"
                >
                  <IconButton
                    aria-label="Previous video"
                    icon={<CaretLeft size={24} />}
                    onClick={onPrevious}
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    pointerEvents="auto"
                  />
                  <IconButton
                    aria-label="Next video"
                    icon={<CaretRight size={24} />}
                    onClick={onNext}
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    pointerEvents="auto"
                  />
                </Flex>
              )}
            </Box>
            
            {/* Video description section - Made more prominent */}
            {videoData?.description && (
              <Box p={5}>
                <Heading size="sm" mb={2}>Description</Heading>
                <Box bg={descriptionBg} p={4} borderRadius="md">
                  <Text color={textColor}>
                    {videoData.description}
                  </Text>
                </Box>
              </Box>
            )}
            
            {/* Features section */}
            {videoData?.predictions?.length > 0 && (
              <Box p={5} pt={videoData?.description ? 2 : 5}>
                <Heading size="sm" mb={2}>Features</Heading>
                <Box bg={featuresBg} p={4} borderRadius="md">
                  <Wrap spacing={2}>
                    {videoData.predictions.map((pred, idx) => {
                      // Map the feature to get proper ID and label
                      const { featureId, label } = mapFeature(pred);
                      
                      return (
                        <WrapItem key={idx}>
                          <Tooltip 
                            label={`${pred.confidence ? `Confidence: ${Math.round(pred.confidence * 100)}% - ` : ''}${pred.source === 'manual' ? 'Manually tagged' : 'Auto-detected'}`} 
                            placement="top" 
                            hasArrow
                          >
                            <Badge 
                              px={2}
                              py={1}
                              borderRadius="full"
                              bg={pred.source === 'manual' ? manualBadgeBg : autoBadgeBg}
                              color={badgeColor}
                              cursor="pointer"
                              _hover={{
                                transform: 'translateY(-1px)',
                                boxShadow: 'sm'
                              }}
                              transition="all 0.2s"
                              onClick={() => {
                                // Close modal and bubble up event to parent
                                onClose();
                                // After slight delay to ensure modal is closed
                                setTimeout(() => {
                                  // Set the filter using the normalized feature ID
                                  const event = new CustomEvent('featureFilter', {
                                    detail: { featureId: normalizeFeature(featureId) }
                                  });
                                  document.dispatchEvent(event);
                                }, 100);
                              }}
                            >
                              {formatFeatureLabel(label || pred.class)}
                            </Badge>
                          </Tooltip>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              </Box>
            )}

            {/* Upload date - De-emphasized */}
            <Box p={5} pt={2}>
              <Divider mb={3} />
              <HStack spacing={2} color={dateColor} fontSize="sm">
                <Clock size={14} />
                <Text>Added on {formatDate(videoData?.uploadedAt)}</Text>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VideoModal;
